import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    CalendarClock,
    Check,
    Download,
    FileText,
    Images,
    Inbox,
    LogOut,
    Mail,
    Paperclip,
    ShieldX,
    StickyNote,
    Trash2,
    Upload,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
        window.location.origin + "/admin",
    )}`;
}

function GoogleG() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path
                fill="#4285F4"
                d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
            />
            <path
                fill="#34A853"
                d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z"
            />
            <path
                fill="#FBBC05"
                d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32z"
            />
            <path
                fill="#EA4335"
                d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z"
            />
        </svg>
    );
}

const MODE_LABEL = { video: "Video call", onsite: "On-site", phone: "Phone call" };

export default function Admin() {
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState(location.state?.user || null);
    const [status, setStatus] = useState(location.state?.user ? "in" : "checking");
    const [enquiries, setEnquiries] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [tab, setTab] = useState("enquiries");
    const [media, setMedia] = useState(null);
    const [aboutPhotoId, setAboutPhotoId] = useState(null);
    const [meetEditorFor, setMeetEditorFor] = useState(null);
    const [draftDt, setDraftDt] = useState("");
    const [draftMode, setDraftMode] = useState("video");
    const [commentFor, setCommentFor] = useState(null);
    const [draftComment, setDraftComment] = useState("");

    useEffect(() => {
        if (user) return;
        let cancelled = false;
        fetch(`${API}/auth/me`, { credentials: "include" })
            .then(async (res) => {
                if (!res.ok) throw new Error("not authenticated");
                const u = await res.json();
                if (!cancelled) {
                    setUser(u);
                    setStatus("in");
                }
            })
            .catch(() => {
                if (!cancelled) setStatus("out");
            });
        return () => {
            cancelled = true;
        };
    }, [user]);

    useEffect(() => {
        if (status !== "in") return;
        let cancelled = false;
        fetch(`${API}/enquiries`, { credentials: "include" })
            .then(async (res) => {
                if (res.status === 403) {
                    if (!cancelled) setForbidden(true);
                    return;
                }
                if (!res.ok) throw new Error("failed to load");
                const data = await res.json();
                if (!cancelled) setEnquiries(data.enquiries);
            })
            .catch(() => {
                if (!cancelled) toast.error("Couldn't load enquiries.");
            });
        fetch(`${API}/settings/public`)
            .then((r) => r.json())
            .then((d) => {
                if (!cancelled) setAboutPhotoId(d.about_photo_file_id || null);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [status]);

    useEffect(() => {
        if (status !== "in" || tab !== "media") return;
        let cancelled = false;
        fetch(`${API}/media`, { credentials: "include" })
            .then(async (res) => {
                if (!res.ok) throw new Error("failed to load");
                const data = await res.json();
                if (!cancelled) setMedia(data.media);
            })
            .catch(() => {
                if (!cancelled) toast.error("Couldn't load media.");
            });
        return () => {
            cancelled = true;
        };
    }, [status, tab]);

    const signOut = async () => {
        try {
            await fetch(`${API}/auth/logout`, {
                method: "POST",
                credentials: "include",
            });
        } catch (e) {
            // ignore network failures on logout
        }
        toast.success("Signed out.");
        navigate("/", { replace: true });
    };

    const downloadCsv = () => {
        const a = document.createElement("a");
        a.href = `${API}/enquiries/export.csv`;
        a.download = "enquiries.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const toggleHandled = async (index) => {
        const enquiry = enquiries[index];
        const next = !enquiry.handled;
        setEnquiries((list) =>
            list.map((e, i) =>
                i === index
                    ? { ...e, handled: next, handled_at: next ? new Date().toISOString() : null }
                    : e,
            ),
        );
        try {
            const res = await fetch(`${API}/enquiries/${enquiry.id}/handled`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ handled: next }),
            });
            if (!res.ok) throw new Error("update failed");
            const data = await res.json();
            setEnquiries((list) =>
                list.map((e, i) => (i === index ? { ...e, ...data.enquiry } : e)),
            );
            toast.success(
                next
                    ? "Marked as replied."
                    : "Marked as awaiting reply.",
            );
        } catch (err) {
            setEnquiries((list) =>
                list.map((e, i) => (i === index ? { ...e, ...enquiry } : e)),
            );
            toast.error("Couldn't update — please try again.");
        }
    };

    const deleteEnquiry = async (index) => {
        const enquiry = enquiries[index];
        if (!window.confirm(`Delete the enquiry from ${enquiry.name}? This can't be undone.`)) {
            return;
        }
        setEnquiries((list) => list.filter((_, i) => i !== index));
        try {
            const res = await fetch(`${API}/enquiries/${enquiry.id}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!res.ok) throw new Error("failed");
            toast.success("Enquiry deleted.");
        } catch (err) {
            setEnquiries((list) => {
                const copy = [...list];
                copy.splice(index, 0, enquiry);
                return copy;
            });
            toast.error("Couldn't delete — please try again.");
        }
    };

    const openMeetEditor = (index) => {        const e = enquiries[index];
        if (meetEditorFor === index) {
            setMeetEditorFor(null);
            return;
        }
        setDraftDt(
            e.meeting_at
                ? new Date(e.meeting_at).toISOString().slice(0, 16)
                : "",
        );
        setDraftMode(e.meeting_mode || "video");
        setMeetEditorFor(index);
    };

    const saveMeeting = async (index, clear = false) => {
        const enquiry = enquiries[index];
        if (!clear && !draftDt) {
            toast.error("Pick a date and time first.");
            return;
        }
        const payload = clear
            ? { meeting_at: null, meeting_mode: null, meeting_note: "" }
            : {
                  meeting_at: new Date(draftDt).toISOString(),
                  meeting_mode: draftMode,
                  meeting_note: "",
              };
        const prev = enquiry;
        setEnquiries((list) =>
            list.map((e, i) => (i === index ? { ...e, ...payload } : e)),
        );
        setMeetEditorFor(null);
        try {
            const res = await fetch(`${API}/enquiries/${enquiry.id}/meeting`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error("failed");
            toast.success(
                clear
                    ? "Meeting cleared."
                    : "Meeting scheduled — download the invite below.",
            );
        } catch (err) {
            setEnquiries((list) =>
                list.map((e, i) => (i === index ? { ...e, ...prev } : e)),
            );
            toast.error("Couldn't save the meeting — please try again.");
        }
    };

    const openNoteEditor = (index) => {
        if (commentFor === index) {
            setCommentFor(null);
            return;
        }
        setDraftComment(enquiries[index].comment || "");
        setCommentFor(index);
    };

    const saveComment = async (index) => {
        const enquiry = enquiries[index];
        const text = draftComment.trim();
        const prev = enquiry;
        setEnquiries((list) =>
            list.map((e, i) => (i === index ? { ...e, comment: text } : e)),
        );
        try {
            const res = await fetch(`${API}/enquiries/${enquiry.id}/comment`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ comment: text }),
            });
            if (!res.ok) throw new Error("failed");
            setCommentFor(null);
            toast.success(text ? "Comment saved." : "Comment cleared.");
        } catch (err) {
            setEnquiries((list) =>
                list.map((e, i) => (i === index ? { ...e, ...prev } : e)),
            );
            toast.error("Couldn't save the comment — please try again.");
        }
    };

    const onMediaUpload = async (e) => {        const picked = Array.from(e.target.files || []);
        e.target.value = "";
        for (const f of picked) {
            if (f.size > 25 * 1024 * 1024) {
                toast.error(`${f.name} is over 25 MB.`);
                continue;
            }
            const fd = new FormData();
            fd.append("file", f);
            try {
                const res = await fetch(`${API}/media`, {
                    method: "POST",
                    credentials: "include",
                    body: fd,
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.detail || "upload failed");
                }
                toast.success(`${f.name} uploaded.`);
            } catch (err) {
                toast.error(`${f.name}: ${err.message}`);
            }
        }
        const refreshed = await fetch(`${API}/media`, { credentials: "include" });
        if (refreshed.ok) setMedia((await refreshed.json()).media);
    };

    const removeMedia = async (m) => {
        try {
            const res = await fetch(`${API}/media/${m.file_id}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!res.ok) throw new Error();
            setMedia((list) => list.filter((x) => x.file_id !== m.file_id));
            if (aboutPhotoId === m.file_id) setAboutPhotoId(null);
            toast.success("Removed from library.");
        } catch (err) {
            toast.error("Couldn't remove — please try again.");
        }
    };

    const setAbout = async (m) => {
        try {
            const res = await fetch(`${API}/media/${m.file_id}/set-about`, {
                method: "POST",
                credentials: "include",
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "failed");
            }
            setAboutPhotoId(m.file_id);
            toast.success("About photo updated on the site.");
        } catch (err) {
            toast.error(err.message || "Couldn't update the About photo.");
        }
    };

    if (status === "checking") {
        return (
            <div className="auth-callback" data-testid="admin-loading">
                <div className="auth-callback-inner">Checking your session…</div>
            </div>
        );
    }

    if (status === "out") {
        return (
            <div className="login-page" data-testid="admin-signin-prompt">
                <a href="/" className="back-link" data-testid="admin-back-link">
                    <ArrowLeft size={14} strokeWidth={1.5} /> Back to site
                </a>
                <div className="auth-card" data-testid="admin-signin-card">
                    <div className="auth-seal">SFG</div>
                    <div className="auth-kicker">PRIVATE AREA</div>
                    <h1 className="auth-title">Enquiries inbox</h1>
                    <p className="auth-copy">
                        Sign in with your Google account to view enquiries
                        submitted through the booking form.
                    </p>
                    <button
                        className="btn-google"
                        data-testid="admin-google-signin-button"
                        onClick={startGoogleSignIn}
                    >
                        <GoogleG /> Continue with Google
                    </button>
                </div>
            </div>
        );
    }

    if (forbidden) {
        return (
            <div className="login-page" data-testid="admin-forbidden">
                <a href="/" className="back-link">
                    <ArrowLeft size={14} strokeWidth={1.5} /> Back to site
                </a>
                <div className="auth-card" data-testid="admin-forbidden-card">
                    <div className="auth-seal auth-seal-brick">
                        <ShieldX size={20} strokeWidth={1.5} />
                    </div>
                    <div className="auth-kicker">ACCESS RESTRICTED</div>
                    <h1 className="auth-title">Not authorized</h1>
                    <p className="auth-copy">
                        You're signed in as {user?.email}, but this account
                        isn't on the allowlist for the enquiries inbox.
                    </p>
                    <button
                        className="btn btn-ghost"
                        data-testid="admin-forbidden-signout"
                        onClick={signOut}
                    >
                        Sign out
                    </button>
                </div>
            </div>
        );
    }

    const unhandled = enquiries ? enquiries.filter((e) => !e.handled).length : 0;

    return (
        <div className="admin-page" data-testid="admin-page">
            <header className="admin-bar">
                <div className="admin-bar-left">
                    <img
                        src="/logo.svg"
                        alt="SoftwareFairGo scales seal"
                        className="logo-mark logo-mark-admin"
                    />
                    <span className="admin-wordmark">
                        SoftwareFairGo<span className="dot">.</span>
                    </span>
                    <span className="admin-bar-label">OWNER AREA</span>
                </div>
                <div className="admin-bar-right">
                    <span className="admin-user" data-testid="admin-user-email">
                        {user?.email}
                    </span>
                    {tab === "enquiries" && (
                        <button
                            className="back-to-top"
                            data-testid="admin-export-csv-button"
                            onClick={downloadCsv}
                        >
                            <Download size={14} strokeWidth={1.5} /> Download CSV
                        </button>
                    )}
                    <a href="/" className="back-link" data-testid="admin-home-link">
                        <ArrowLeft size={14} strokeWidth={1.5} /> Site
                    </a>
                    <button
                        className="back-to-top"
                        data-testid="admin-signout-button"
                        onClick={signOut}
                    >
                        Sign out <LogOut size={14} strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <main className="admin-main">
                <div className="admin-tabs" data-testid="admin-tabs">
                    <button
                        className={`admin-tab-btn ${tab === "enquiries" ? "active" : ""}`}
                        data-testid="admin-tab-enquiries"
                        onClick={() => setTab("enquiries")}
                    >
                        <Inbox size={14} strokeWidth={1.5} /> Enquiries
                    </button>
                    <button
                        className={`admin-tab-btn ${tab === "media" ? "active" : ""}`}
                        data-testid="admin-tab-media"
                        onClick={() => setTab("media")}
                    >
                        <Images size={14} strokeWidth={1.5} /> Media
                    </button>
                </div>

                {tab === "enquiries" && (
                    <>
                        <div className="admin-count" data-testid="admin-enquiry-count">
                            {enquiries === null
                                ? "Loading enquiries…"
                                : enquiries.length === 0
                                  ? "No enquiries yet"
                                  : `${enquiries.length} ${enquiries.length === 1 ? "enquiry" : "enquiries"} · ${unhandled} awaiting reply`}
                        </div>

                        {enquiries !== null && enquiries.length === 0 && (
                            <div className="admin-empty" data-testid="admin-empty-state">
                                No enquiries yet — they'll appear here the moment a
                                visitor sends one from the booking form.
                            </div>
                        )}

                        {enquiries !== null && enquiries.length > 0 && (
                            <div className="admin-table" data-testid="admin-enquiries-table">
                                <div className="admin-row admin-head">
                                    <span>Done</span>
                                    <span>Received</span>
                                    <span>From</span>
                                    <span>Service</span>
                                    <span>Details &amp; follow-up</span>
                                </div>
                                {enquiries.map((e, i) => {
                                    const replyHref = `mailto:${e.email}?subject=${encodeURIComponent(
                                        "Re: your enquiry — SoftwareFairGo",
                                    )}&body=${encodeURIComponent(
                                        `Hi ${e.name.split(" ")[0]},\n\nThanks for your enquiry${
                                            e.business ? ` from ${e.business}` : ""
                                        } about ${e.service}.\n\nHappy to walk you through it — would a short call or meeting suit? Propose a time and I'll confirm.\n\nBest regards,\nSoftwareFairGo`,
                                    )}`;
                                    return (
                                        <div
                                            className={`admin-row ${e.handled ? "handled" : ""}`}
                                            key={e.id}
                                            data-testid={`admin-row-${i}`}
                                        >
                                            <button
                                                className={`tick-btn ${e.handled ? "ticked" : ""}`}
                                                data-testid={`admin-toggle-handled-${i}`}
                                                title={e.handled ? "Mark as awaiting reply" : "Mark as replied"}
                                                aria-pressed={e.handled}
                                                onClick={() => toggleHandled(i)}
                                            >
                                                <Check size={13} strokeWidth={2} />
                                            </button>
                                            <span className="admin-date">
                                                {new Date(e.created_at).toLocaleDateString(
                                                    "en-AU",
                                                    {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    },
                                                )}
                                                {e.handled && (
                                                    <span className="replied-badge">REPLIED</span>
                                                )}
                                            </span>
                                            <span className="admin-from">
                                                <b>{e.name}</b>
                                                {e.business ? ` · ${e.business}` : ""}
                                                <small>{e.email}</small>
                                            </span>
                                            <span className="admin-service">{e.service}</span>
                                            <div className="admin-details">
                                                {e.details || "—"}
                                                {e.comment ? (
                                                    <span
                                                        className="enq-comment"
                                                        data-testid={`enquiry-comment-${i}`}
                                                    >
                                                        <b>YOUR NOTE</b>
                                                        {e.comment}
                                                    </span>
                                                ) : null}
                                                {e.attachments?.length > 0 && (
                                                    <span className="attach-list">
                                                        {e.attachments.map((a) => (
                                                            <a
                                                                key={a.file_id}
                                                                className="file-chip"
                                                                href={`${API}/files/${a.file_id}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                data-testid={`attachment-${a.file_id.slice(-4)}`}
                                                            >
                                                                <Paperclip size={11} strokeWidth={1.5} />{" "}
                                                                {a.name}
                                                            </a>
                                                        ))}
                                                    </span>
                                                )}
                                                <div className="row-actions">
                                                    <a
                                                        className="btn-mini"
                                                        data-testid={`admin-reply-${i}`}
                                                        href={replyHref}
                                                    >
                                                        <Mail size={12} /> Reply
                                                    </a>
                                                    <button
                                                        className="btn-mini"
                                                        data-testid={`admin-schedule-${i}`}
                                                        onClick={() => openMeetEditor(i)}
                                                    >
                                                        <CalendarClock size={12} />{" "}
                                                        {e.meeting_at ? "Reschedule" : "Schedule"}
                                                    </button>
                                                    {e.meeting_at && (
                                                        <>
                                                            <span
                                                                className="meeting-chip"
                                                                data-testid={`meeting-${i}`}
                                                            >
                                                                {new Date(e.meeting_at).toLocaleString(
                                                                    "en-AU",
                                                                    {
                                                                        weekday: "short",
                                                                        day: "2-digit",
                                                                        month: "short",
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                    },
                                                                )}{" "}
                                                                · {MODE_LABEL[e.meeting_mode] || e.meeting_mode}
                                                            </span>
                                                            <a
                                                                className="btn-mini"
                                                                data-testid={`admin-ics-${i}`}
                                                                href={`${API}/enquiries/${e.id}/invite.ics`}
                                                                download="meeting-invite.ics"
                                                            >
                                                                <Download size={12} /> .ics
                                                            </a>
                                                        </>
                                                    )}
                                                    <button
                                                        className={`btn-mini ${commentFor === i ? "btn-mini-active" : ""}`}
                                                        data-testid={`admin-note-${i}`}
                                                        title={
                                                            e.comment
                                                                ? "Edit your note on this enquiry"
                                                                : "Add a note to this enquiry"
                                                        }
                                                        onClick={() => openNoteEditor(i)}
                                                    >
                                                        <StickyNote size={12} />{" "}
                                                        {e.comment ? "Note ✓" : "Note"}
                                                    </button>
                                                    <button
                                                        className="btn-mini btn-mini-danger"
                                                        data-testid={`admin-delete-${i}`}
                                                        title="Delete this enquiry"
                                                        onClick={() => deleteEnquiry(i)}
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                                {meetEditorFor === i && (
                                                    <div
                                                        className="meet-editor"
                                                        data-testid={`meet-editor-${i}`}
                                                    >
                                                        <input
                                                            type="datetime-local"
                                                            data-testid={`meet-dt-${i}`}
                                                            value={draftDt}
                                                            onChange={(ev) =>
                                                                setDraftDt(ev.target.value)
                                                            }
                                                        />
                                                        <select
                                                            data-testid={`meet-mode-${i}`}
                                                            value={draftMode}
                                                            onChange={(ev) =>
                                                                setDraftMode(ev.target.value)
                                                            }
                                                        >
                                                            <option value="video">Video call</option>
                                                            <option value="onsite">On-site</option>
                                                            <option value="phone">Phone call</option>
                                                        </select>
                                                        <button
                                                            className="btn-mini btn-mini-active"
                                                            data-testid={`meet-save-${i}`}
                                                            onClick={() => saveMeeting(i)}
                                                        >
                                                            <Check size={12} /> Save
                                                        </button>
                                                        {e.meeting_at && (
                                                            <button
                                                                className="btn-mini btn-mini-danger"
                                                                data-testid={`meet-clear-${i}`}
                                                                onClick={() => saveMeeting(i, true)}
                                                            >
                                                                Clear
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {commentFor === i && (
                                                    <div
                                                        className="note-editor"
                                                        data-testid={`note-editor-${i}`}
                                                    >
                                                        <textarea
                                                            data-testid={`note-text-${i}`}
                                                            maxLength={2000}
                                                            placeholder="Add a comment for this enquiry (e.g. quoted, follow-up Friday)…"
                                                            value={draftComment}
                                                            onChange={(ev) =>
                                                                setDraftComment(ev.target.value)
                                                            }
                                                        />
                                                        <span className="char-count">
                                                            {draftComment.length}/2000
                                                        </span>
                                                        <button
                                                            className="btn-mini btn-mini-active"
                                                            data-testid={`note-save-${i}`}
                                                            onClick={() => saveComment(i)}
                                                        >
                                                            <Check size={12} /> Save
                                                        </button>
                                                        <button
                                                            className="btn-mini"
                                                            data-testid={`note-cancel-${i}`}
                                                            onClick={() => setCommentFor(null)}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {tab === "media" && (
                    <div data-testid="media-library">
                        <div className="media-toolbar">
                            <label className="upload-btn" data-testid="media-upload-button">
                                <Upload size={14} strokeWidth={1.5} /> Upload files
                                <input
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={onMediaUpload}
                                    data-testid="media-upload-input"
                                />
                            </label>
                            <span className="admin-count">
                                Any file type · up to 25 MB each
                            </span>
                        </div>

                        <div className="media-grid">
                            {media === null && (
                                <div className="admin-empty">Loading media…</div>
                            )}
                            {media !== null && media.length === 0 && (
                                <div className="admin-empty" data-testid="media-empty-state">
                                    Nothing uploaded yet. Images you upload here can
                                    become the About section photo on the site.
                                </div>
                            )}
                            {media !== null &&
                                media.map((m) => (
                                    <div
                                        className="media-card"
                                        key={m.file_id}
                                        data-testid={`media-card-${m.file_id.slice(-6)}`}
                                    >
                                        {m.content_type?.startsWith("image/") ? (
                                            <img
                                                className="media-thumb"
                                                src={`${API}/media/${m.file_id}`}
                                                alt={m.original_filename}
                                            />
                                        ) : (
                                            <div className="media-doc">
                                                <FileText size={22} strokeWidth={1.5} />
                                                <small>{m.original_filename}</small>
                                            </div>
                                        )}
                                        <div className="media-actions">
                                            {m.content_type?.startsWith("image/") && (
                                                <button
                                                    className={`btn-mini ${aboutPhotoId === m.file_id ? "btn-mini-active" : ""}`}
                                                    data-testid={`media-set-about-${m.file_id.slice(-6)}`}
                                                    onClick={() => setAbout(m)}
                                                >
                                                    {aboutPhotoId === m.file_id ? (
                                                        <>
                                                            <Check size={12} /> About photo
                                                        </>
                                                    ) : (
                                                        "Set as About photo"
                                                    )}
                                                </button>
                                            )}
                                            <button
                                                className="btn-mini btn-mini-danger"
                                                data-testid={`media-delete-${m.file_id.slice(-6)}`}
                                                onClick={() => removeMedia(m)}
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                        <div className="media-name">{m.original_filename}</div>
                                        <div className="media-size">
                                            {(m.size / 1024 / 1024).toFixed(2)} MB
                                        </div>
                                    </div>
                                ))}
                        </div>

                        <div className="media-note" data-testid="media-about-note">
                            Current About photo on the site:{" "}
                            {aboutPhotoId
                                ? "an image you uploaded"
                                : "default team photo (team.png)"}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
